import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import { useTranslation } from "react-i18next";
import { isOrderApproved, OrderStatus } from "../../types/order";
import {
  areAllStandbyExpenseReviewItemsChecked,
  type StandbyOrder,
} from "../../types/standbyOrder";
import DisabledButtonTooltip from "../common/DisabledButtonTooltip";
import DetailSectionCard from "../common/DetailSectionCard";
import StandbyOrderDetailPanel from "../standbyOrders/StandbyOrderDetailPanel";
import AddStandbyOrdersToReportDialog from "./AddStandbyOrdersToReportDialog";
import StandbyApproveOverrideDialog from "./StandbyApproveOverrideDialog";
import ReportOrderCardFinanceActionsMenu from "./ReportOrderCardFinanceActionsMenu";
import { createReportCardFinanceActions } from "./reportCardFinanceActions";
import { UangJalanReportStatus, type UangJalanReport } from "../../types/uangJalanReport";
import type translations from "../../translations/translations.json";
import { TestId } from "../../constants/testIds";
import { canMutateOrders, isFinanceApprover, isFinance, isUserAdmin } from "../../types/user";
import type { User } from "../../types/user";
import { verifyUangJalanReportApi } from "../../api/uangJalanReportsApi";
import {
  approveStandbyOrderApi,
  requestRevisionStandbyOrderApi,
  updateStandbyExpenseReviewChecklistApi,
  verifyStandbyOrderApi,
} from "../../api/standbyOrdersApi";
import { toastEmitter } from "../../utils/toastEmitter";
import {
  cascadeReportVerifyIfReady,
  emitOrderAutoVerifiedToast,
  runAutoVerifyAfterChecklistComplete,
} from "../../utils/uangJalanReportAutoVerify";

type TranslationKeys = keyof typeof translations.en;

interface UangJalanReportStandbySectionProps {
  report: UangJalanReport;
  user: User | null;
  onReportChange: () => Promise<UangJalanReport | null>;
  onStandbyUpdated?: (order: StandbyOrder) => void;
  onActionError: (message: string) => void;
}

const UangJalanReportStandbySection: React.FC<UangJalanReportStandbySectionProps> = ({
  report,
  user,
  onReportChange,
  onStandbyUpdated,
  onActionError,
}) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(false);
  const [checklistLoadingId, setChecklistLoadingId] = useState<string | null>(null);
  const [reviseDialogOrderId, setReviseDialogOrderId] = useState<string | null>(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [approveOverrideOrder, setApproveOverrideOrder] = useState<StandbyOrder | null>(null);

  const orders = report.standbyOrders ?? [];
  const userCanMutate = !!user && canMutateOrders(user);
  const userCanFinance = !!user && (isFinance(user) || isUserAdmin(user));
  const userCanApprove = !!user && (isFinanceApprover(user) || isUserAdmin(user));
  const reportIsOpen = report.status === UangJalanReportStatus.OPEN;
  const reportInReview = report.status === UangJalanReportStatus.IN_REVIEW;
  const reportIsVerified = report.status === UangJalanReportStatus.VERIFIED;
  const checklistEnabled = userCanFinance && reportInReview;
  const reportBlocksRevision =
    report.status === UangJalanReportStatus.VERIFIED ||
    report.status === UangJalanReportStatus.APPROVED ||
    report.status === UangJalanReportStatus.APPROVED_WITH_OVERRIDE;

  const canAdd = userCanMutate && reportIsOpen;
  const addDisabledHint = !userCanMutate
    ? t("reports.addOrdersDisabledRole" as TranslationKeys)
    : !reportIsOpen
      ? t("reports.addOrdersDisabledHint" as TranslationKeys)
      : "";

  const runAction = async (
    action: () => Promise<StandbyOrder>,
    successMessage: TranslationKeys,
  ) => {
    setActionLoading(true);
    try {
      await action();
      let refreshedReport = await onReportChange();
      if (successMessage === "orders.verifiedSuccessfully") {
        refreshedReport = await cascadeReportVerifyIfReady(
          refreshedReport,
          verifyUangJalanReportApi,
          onReportChange,
        );
        emitOrderAutoVerifiedToast(t, refreshedReport);
      } else {
        toastEmitter.emit(t(successMessage), "success");
      }
    } catch (err: any) {
      onActionError(err?.response?.data?.error?.message || err.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChecklistToggle = async (orderId: string, itemKey: string, checked: boolean) => {
    setChecklistLoadingId(orderId);
    try {
      const updated = await updateStandbyExpenseReviewChecklistApi(orderId, {
        key: itemKey,
        checked,
      });
      const refreshedReport = await runAutoVerifyAfterChecklistComplete({
        order: updated,
        checked,
        isChecklistComplete: (member) =>
          areAllStandbyExpenseReviewItemsChecked(member as StandbyOrder),
        verifyMember: verifyStandbyOrderApi,
        refreshReport: onReportChange,
        verifyReport: verifyUangJalanReportApi,
      });
      if (refreshedReport !== null) {
        emitOrderAutoVerifiedToast(t, refreshedReport);
      } else {
        onStandbyUpdated?.(updated);
      }
    } catch (err: any) {
      onActionError(err?.response?.data?.error?.message || err.message || "Action failed");
    } finally {
      setChecklistLoadingId(null);
    }
  };

  const verifiedCount = orders.filter(
    (order) => order.status === OrderStatus.VERIFIED || isOrderApproved(order.status),
  ).length;

  const getVerifyOrderDisabledHint = (
    order: StandbyOrder,
    allChecklistComplete: boolean,
  ): string => {
    if (!userCanFinance) {
      return t("permission.financeOnly" as TranslationKeys);
    }
    if (!reportInReview) {
      return t("reports.financeActionsDisabledStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.IN_REVIEW) {
      return t("reports.verifyOrderDisabledStatus" as TranslationKeys);
    }
    if (!allChecklistComplete) {
      return t("reports.verifyOrderDisabledHint" as TranslationKeys);
    }
    return "";
  };

  const getApproveOrderDisabledHint = (order: StandbyOrder): string => {
    if (!userCanApprove) {
      return t("permission.approverOnly" as TranslationKeys);
    }
    if (!reportIsVerified) {
      return t("reports.approveOrderDisabledReportStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.VERIFIED) {
      return t("reports.approveOrderDisabledStatus" as TranslationKeys);
    }
    return "";
  };

  const getReviseOrderDisabledHint = (order: StandbyOrder): string => {
    if (!userCanFinance) {
      return t("permission.financeOnly" as TranslationKeys);
    }
    if (reportBlocksRevision) {
      return t("reports.reviseOrderDisabledReportStatus" as TranslationKeys);
    }
    if (order.status !== OrderStatus.IN_REVIEW && order.status !== OrderStatus.VERIFIED) {
      return t("reports.reviseOrderDisabledStatus" as TranslationKeys);
    }
    return "";
  };

  return (
    <Box sx={{ mt: 3 }} data-testid={TestId.UJR.STANDBY_SECTION}>
      <DetailSectionCard
        title={t("reports.standbySection" as TranslationKeys)}
        icon={<PauseCircleOutlinedIcon fontSize="small" />}
        iconColor="#0F766E"
        sx={{ mb: 0.75 }}
        headerMeta={
          <Typography variant="body2" color="text.secondary">
            {t("reports.standbySectionMeta" as TranslationKeys, {
              count: orders.length,
              verified: verifiedCount,
            })}
          </Typography>
        }
        headerActions={
          <DisabledButtonTooltip title={addDisabledHint} disabled={!canAdd}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              disabled={!canAdd}
              onClick={() => setAddDialogOpen(true)}
              data-testid={TestId.UJR.ADD_STANDBY}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              {t("reports.addStandby" as TranslationKeys)}
            </Button>
          </DisabledButtonTooltip>
        }
      />

      <Stack spacing={1}>
        {orders.map((order) => {
          const allChecklistComplete = areAllStandbyExpenseReviewItemsChecked(order);
          const canVerifyOrder =
            userCanFinance &&
            reportInReview &&
            order.status === OrderStatus.IN_REVIEW &&
            allChecklistComplete;
          const canReviseOrder =
            userCanFinance &&
            !reportBlocksRevision &&
            (order.status === OrderStatus.IN_REVIEW || order.status === OrderStatus.VERIFIED);
          const canApproveOrder =
            userCanApprove && reportIsVerified && order.status === OrderStatus.VERIFIED;
          const verifyDisabledHint = getVerifyOrderDisabledHint(order, allChecklistComplete);
          const reviseDisabledHint = getReviseOrderDisabledHint(order);
          const approveDisabledHint = getApproveOrderDisabledHint(order);

          const financeActions = (
            <ReportOrderCardFinanceActionsMenu
              menuTestId={TestId.UJR.STANDBY_CARD_ACTIONS_MENU(order._id)}
              menuId={`ujr-standby-card-actions-menu-${order._id}`}
              ariaLabel={t("Actions" as TranslationKeys)}
              actions={createReportCardFinanceActions({
                t,
                actionLoading,
                userCanApprove,
                canVerify: canVerifyOrder,
                canRevise: canReviseOrder,
                canApprove: canApproveOrder,
                verifyDisabledHint,
                reviseDisabledHint,
                approveDisabledHint,
                onVerify: () =>
                  runAction(() => verifyStandbyOrderApi(order._id), "orders.verifiedSuccessfully"),
                onRevise: () => setReviseDialogOrderId(order._id),
                onApprove: () =>
                  runAction(() => approveStandbyOrderApi(order._id), "orders.approvedSuccessfully"),
                onApproveOverride: () => setApproveOverrideOrder(order),
                testIds: {
                  verify: TestId.UJR.VERIFY_STANDBY(order._id),
                  revise: TestId.UJR.REVISE_STANDBY(order._id),
                  approve: TestId.UJR.APPROVE_STANDBY(order._id),
                  approveOverride: TestId.UJR.APPROVE_OVERRIDE_STANDBY(order._id),
                },
              })}
            />
          );

          return (
            <StandbyOrderDetailPanel
              key={order._id}
              order={order}
              variant="card"
              rootTestId={TestId.UJR.STANDBY_ROW}
              checklist={{
                disabled: !checklistEnabled || order.status !== OrderStatus.IN_REVIEW,
                onToggle: handleChecklistToggle,
                loading: checklistLoadingId === order._id,
              }}
              secondaryLink={{ type: "order-detail", openInNewTab: true }}
              financeActions={financeActions}
            />
          );
        })}
      </Stack>

      {userCanMutate && (
        <AddStandbyOrdersToReportDialog
          report={report}
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onSuccess={async () => {
            await onReportChange();
          }}
        />
      )}

      {userCanApprove && (
        <StandbyApproveOverrideDialog
          order={approveOverrideOrder}
          open={approveOverrideOrder !== null}
          onClose={() => setApproveOverrideOrder(null)}
          onSuccess={async () => {
            await onReportChange();
          }}
        />
      )}

      <Dialog
        open={reviseDialogOrderId !== null}
        onClose={() => {
          setReviseDialogOrderId(null);
          setRevisionNote("");
        }}
        fullWidth
        maxWidth="sm"
        data-testid={TestId.UJR.REVISION_DIALOG}
      >
        <DialogTitle>{t("orders.requestRevision" as TranslationKeys)}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            required
            label={t("orders.revisionNote" as TranslationKeys)}
            value={revisionNote}
            onChange={(event) => setRevisionNote(event.target.value)}
            inputProps={{ "data-testid": TestId.UJR.REVISION_NOTE }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setReviseDialogOrderId(null);
              setRevisionNote("");
            }}
          >
            {t("Cancel" as TranslationKeys)}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={actionLoading || !revisionNote.trim()}
            onClick={async () => {
              if (!reviseDialogOrderId) {
                return;
              }
              await runAction(
                () => requestRevisionStandbyOrderApi(reviseDialogOrderId, revisionNote.trim()),
                "orders.revisionRequestedSuccessfully",
              );
              setReviseDialogOrderId(null);
              setRevisionNote("");
            }}
            data-testid={TestId.UJR.REVISION_SUBMIT}
          >
            {t("orders.requestRevision" as TranslationKeys)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UangJalanReportStandbySection;
